import { getCurrentWindow } from '@tauri-apps/api/window';
import dynamic from 'next/dynamic';

import { useSyncCuaReadinessGate } from '@/features/rpa/cua-readiness-gate-sync';
import { useSyncCuaWakeAssertionPreference } from '@/features/rpa/cua-wake-assertion-preference-sync';
import { DesktopPermissionGateHost } from '@/features/rpa/desktop-permission-gate';
import {
  rpaRecorderBridge,
  type RpaRecorderBridge,
} from '@/features/rpa/recorder/api/recorder-bridge';
import { OverlayRecordingNameContext } from '@/features/rpa/recorder/components/overlay/overlay-recording-session-hooks';
import { useStopAndName } from '@/features/rpa/recorder/hooks/recordingNameSession';
import { useAgentRecordingPermissionBridge } from '@/features/rpa/recorder/hooks/useAgentRecordingPermissionBridge';
import { RECORDER_ENTRY_SURFACE } from '@/features/rpa/recorder/lib/recording-entry-surface';
import { RpaRecorderLifecycleProvider } from '@/features/rpa/recorder/lifecycle/rpa-recorder-lifecycle';
import { readRecordingEntrySurface } from '@/features/rpa/recorder/model/recording-entry-surface-store';
import { MainWindowOpenProjectListener } from '@/hooks/overlay/openProjectInHeidi';
import { isTauri } from '@/tauri/utils';
import { RPA_MAIN_WINDOW_LABEL } from '@heidi/plugin-core';

// Lazy-load the naming dialog so the heavy Dialog/NamingDialog module is only
// pulled in when a desktop-app recording actually needs naming in the main
// window. See `.ai-context/lazy-modals.md` (Pattern A — parent-gated modal).
const RecordStopDialog = dynamic(
  () =>
    import('@/features/rpa/recorder/components/main-window/record-stop-dialog').then(
      (mod) => mod.RecordStopDialog
    ),
  { ssr: false, loading: () => null }
);

/**
 * Global mount point for RPA recorder side effects and lightweight global RPA UI.
 *
 * Mount once in `_app.tsx` so desktop lifecycle and cross-window navigation
 * run for the full app session, independent of the active route.
 *
 * - {@link RpaRecorderLifecycleProvider} — upload bridge init, persisted
 *   recording hydration, and upload/polling resume (main window only).
 * - {@link DesktopPermissionGateHost} — renders permission prompts opened via
 *   Jotai-backed `useDesktopPermissionGate()` calls.
 * - {@link MainWindowOpenProjectListener} — routes the main window to a
 *   project chat when the overlay agent surface fires "Open in Heidi".
 * - {@link AgentRecordingPermissionEffects} — answers correlated agent
 *   permission requests through the gate host above. Only mounted
 *   here, in the main window — Rust rejects acks from any other webview.
 * - {@link MainWindowRecordingNaming} — shows the recording naming dialog in
 *   the main window for recordings armed from the desktop-app surface (the
 *   overlay is hidden on that path, so it can't host the prompt).
 * - {@link CuaWakeAssertionPreferenceSync} — pushes the keep-awake preference
 *   into Rust.
 * - {@link CuaReadinessGateSync} pushes the readiness kill switch into Rust.
 */
export function RpaRecorderRootEffects() {
  return (
    <RpaRecorderLifecycleProvider>
      <DesktopPermissionGateHost
        variant={{
          default: 'dialog',
          computerUse: 'overlay-card',
        }}
      />
      <AgentRecordingPermissionEffects />
      <CuaWakeAssertionPreferenceSync />
      <CuaReadinessGateSync />
      <MainWindowRecordingNaming />
      <MainWindowOpenProjectListener />
    </RpaRecorderLifecycleProvider>
  );
}

function AgentRecordingPermissionEffects() {
  useAgentRecordingPermissionBridge();
  return null;
}

function CuaWakeAssertionPreferenceSync() {
  useSyncCuaWakeAssertionPreference();
  return null;
}

function CuaReadinessGateSync() {
  useSyncCuaReadinessGate();
  return null;
}

/**
 * Hosts the recording naming dialog in the main window for desktop-app
 * recordings. `useStopAndName` is always mounted (even in the overlay/toolbar
 * webviews where this renders but gates to null) so the cross-window stop
 * decision subscription stays live. The gate shows only in the main window,
 * only for `desktopApp`-armed sessions, only while a naming prompt is open.
 *
 * `useStopAndName` uses a module-level jotai atom, so each webview gets its
 * own instance — the subscription is harmless in non-main windows.
 */
export function MainWindowRecordingNaming({
  bridge = rpaRecorderBridge,
}: {
  readonly bridge?: RpaRecorderBridge;
} = {}) {
  const naming = useStopAndName(bridge);
  const shouldShow =
    isTauri() &&
    getCurrentWindow().label === RPA_MAIN_WINDOW_LABEL &&
    naming.isNamingPromptOpen &&
    readRecordingEntrySurface() === RECORDER_ENTRY_SURFACE.DESKTOP_APP;

  if (!shouldShow) return null;

  return (
    <OverlayRecordingNameContext.Provider value={naming}>
      <RecordStopDialog />
    </OverlayRecordingNameContext.Provider>
  );
}
